import { OffsetUnit } from "@sp-crm/core";
import { GetWorkflowSettingsResult } from "generated/graphql";
import React, { useMemo } from "react";
import { pluralize } from "util/pluralize";
import {
    computeWorkflowDelay,
    hasScheduleTrigger,
    resolveWorkflowSetting,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface WorkflowDelaySummaryProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
}

const fieldPatterns: Record<string, string> = {
    "{{client.moveTimelineActual}}": "the move date",
    "{{client.statusCheckLaterDate}}": "the check back later date",
    "{{homeCareLead.actualStartDate}}": "the actual care start date",
};

const unitLabels: Record<string, string> = {
    d: "day",
    h: "hour",
    m: "minute",
};

const describeDelayRecipe = (
    delayRecipe: string | null,
    workflow: EditableWorkflow,
    workflowSettings: GetWorkflowSettingsResult,
): string | null => {
    if (!delayRecipe) return null;

    for (const [token, label] of Object.entries(fieldPatterns)) {
        if (delayRecipe.includes(token)) {
            const fixedOffsetMatch = delayRecipe.match(/\+(\d+)([dhm])$/);
            if (fixedOffsetMatch) {
                const [, num, unit] = fixedOffsetMatch;
                const numValue = parseInt(num, 10);
                const unitName = unitLabels[unit] || unit;
                return `${num} ${pluralize(numValue, unitName)} after ${label}`;
            }

            const settingsMatch = delayRecipe.match(/\+\{\{settings\.(\w+)\}\}([dhm])$/);
            if (settingsMatch && workflowSettings) {
                const [, settingKey, unit] = settingsMatch;
                const resolvedValue = resolveWorkflowSetting(
                    workflowSettings,
                    workflow,
                    settingKey,
                );

                if (resolvedValue) {
                    const numValue = parseInt(resolvedValue, 10);
                    const unitName = unitLabels[unit] || unit;
                    return `${resolvedValue} ${pluralize(numValue, unitName)} after ${label}`;
                }

                return `After ${label} (configurable delay)`;
            }
        }
    }

    return null;
};

interface DelayAnchor {
    withoutWait: string;
    afterWait: string;
}

const TRIGGER_ANCHOR: DelayAnchor = {
    withoutWait: "Immediately",
    afterWait: "after trigger",
};

const DAILY_CHECK_ANCHOR: DelayAnchor = {
    withoutWait: "When the daily check runs",
    afterWait: "after the daily check runs",
};

const PREVIOUS_ACTION_ANCHOR: DelayAnchor = {
    withoutWait: "Right after the previous action",
    afterWait: "after the previous action",
};

const formatDelay = (delay: number, unit: OffsetUnit, anchor: DelayAnchor): string => {
    if (delay === 0) {
        return anchor.withoutWait;
    }

    const unitName = unitLabels[unit] || unit;
    return `${delay} ${pluralize(delay, unitName)} ${anchor.afterWait}`;
};

export const describeActionInterval = (delayRecipe: string | null): string => {
    const [delay, unit] = computeWorkflowDelay({ delay: 0, delayRecipe });
    return formatDelay(delay, unit, PREVIOUS_ACTION_ANCHOR);
};

export const useWorkflowDelayDescription = (
    workflow: EditableWorkflow,
    workflowSettings: GetWorkflowSettingsResult,
): string | null => {
    return useMemo(() => {
        const recipeDescription = describeDelayRecipe(
            workflow.delayRecipe,
            workflow,
            workflowSettings,
        );
        if (recipeDescription) {
            return recipeDescription;
        }

        const [delay, unit] = computeWorkflowDelay(workflow);
        return formatDelay(
            delay,
            unit,
            hasScheduleTrigger(workflow) ? DAILY_CHECK_ANCHOR : TRIGGER_ANCHOR,
        );
    }, [workflow, workflowSettings]);
};

export const WorkflowDelaySummaryListItem: React.FC<WorkflowDelaySummaryProps> = ({
    workflow,
    workflowSettings,
}) => {
    const delayDescription = useWorkflowDelayDescription(workflow, workflowSettings);

    if (!delayDescription) {
        return null;
    }

    return (
        <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
                <span className="font-medium">Timing:</span> {delayDescription}
            </span>
        </li>
    );
};
